/**
 * Org context — resolving which organisation the user is acting in.
 *
 * **This module exists because the template app once shipped without it.**
 * `setOrgId` had no caller: nothing ever fetched the user's organisations, so
 * `getOrgId()` was always null and every signed-in user landed on "Choose an
 * organisation" — with no switcher to choose from. A dead end for everybody,
 * found by running it (ProGST, 2026-08-25).
 *
 * The precedence below is copy-adapted from `apps/store`'s, which in turn came
 * from `apps/launcher` after its missing-header 400s. The rule it encodes:
 * **resolving and remembering are one act.** Resolve without persisting and
 * the next page load resolves differently; persist without checking membership
 * and a user removed from an org is stuck on it forever.
 *
 * The prototype's `lib/orgs.ts` kept the selection in the shell's parent-domain
 * cookie with `prodoc.org` as a fallback. The per-app key is deliberate here
 * (ADR-0007 §3): storage is per-origin, and one app's selection must never be
 * revived from another's.
 */

import { ORGS_API } from './config'
import { session } from './session'

export interface OrgRow {
  id: string
  display_name: string
  role: string
  is_default_org?: boolean
}

/**
 * Our own key. Per-app because storage is per-origin, and because two apps
 * remembering "the current org" for each other is how one app's switch
 * silently moves another.
 */
export const SELECTED_ORG_KEY = 'fiveox.prodoc_org'

export async function fetchOrgs(): Promise<OrgRow[]> {
  if (!session.getSession()) return []
  try {
    // `session.authed` owns the bearer and the single serialized refresh, so a
    // token that expired while this page was open recovers here rather than
    // rendering an org-less app.
    const res = await session.authed(ORGS_API)
    if (!res.ok) return []
    const body = (await res.json()) as { data?: OrgRow[] }
    return body.data ?? []
  } catch {
    // Org-service unreachable. Not a dead session, and not a reason to sign
    // anyone out — the caller renders "we can't reach your organisations".
    return []
  }
}

/**
 * One member of the organisation, as `core-org-management` lists them —
 * **its** directory, read for a picker and never copied (constitution §1:
 * membership is core's). ProDoc only narrows which workspaces a member sees.
 */
export interface OrgMember {
  user_id: string
  full_name: string | null
  email: string | null
  /** The org role. It grants nothing in ProDoc; the evaluate answer does. */
  role: string
}

/**
 * The organisation's active members — org-service's
 * `GET /api/v1/orgs/{org}/members`, which the edge serves at
 * `${ORGS_API}/{org}/members` (`/api/orgs` → `/api/v1/orgs`,
 * `repo:fiveox:infra/compose/nginx/api-locations.conf`). Any member may read
 * it. **Throws** on a refusal or no answer, so a picker can say it could not
 * list people rather than show an empty organisation.
 */
export async function fetchOrgMembers(orgId: string): Promise<OrgMember[]> {
  const res = await session.authed(`${ORGS_API}/${encodeURIComponent(orgId)}/members`)
  const body = (await res.json().catch(() => null)) as {
    data?: { members?: Partial<OrgMember>[] }
    error?: { message?: string }
  } | null
  if (!res.ok || !body?.data) {
    throw new Error(body?.error?.message ?? `The organisation’s members could not be read (${res.status}).`)
  }
  return (body.data.members ?? [])
    .filter((m): m is Partial<OrgMember> & { user_id: string } => typeof m.user_id === 'string')
    .map((m) => ({ user_id: m.user_id, full_name: m.full_name ?? null, email: m.email ?? null, role: m.role ?? 'user' }))
}

export function getSelectedOrgId(): string | null {
  try {
    return localStorage.getItem(SELECTED_ORG_KEY)
  } catch {
    return null
  }
}

export function setSelectedOrgId(orgId: string | null): void {
  try {
    if (orgId) localStorage.setItem(SELECTED_ORG_KEY, orgId)
    else localStorage.removeItem(SELECTED_ORG_KEY)
  } catch {
    /* private mode; the in-memory selection still works for this tab */
  }
}

/**
 * An org handed over in the URL (`?org=<id>`), stripped once read. Another app
 * — or an invitation accepted in the console — can open ProDoc in a specific
 * organisation this way; the prototype's `adoptOrgParam` is kept for it. The
 * membership check in `resolveSelected` still applies: a handed-over org the
 * user does not belong to is ignored.
 */
export function takeOrgParam(): string | null {
  try {
    const url = new URL(window.location.href)
    const org = url.searchParams.get('org')
    if (!org) return null
    url.searchParams.delete('org')
    window.history.replaceState(null, '', url.pathname + url.search + url.hash)
    return org
  } catch {
    return null
  }
}

/**
 * Which org opens first.
 *
 * 1. The one previously chosen — **but only if the user is still a member**.
 *    Skipping that check leaves someone removed from an org permanently stuck
 *    on it, because every request 404s and nothing offers a way out.
 * 2. Otherwise the user's default org.
 * 3. Otherwise the first membership.
 *
 * Returns null only when the user genuinely belongs to no organisation.
 */
export function resolveSelected(orgs: OrgRow[]): OrgRow | null {
  if (orgs.length === 0) return null
  const remembered = getSelectedOrgId()
  const stillAMember = remembered ? orgs.find((o) => o.id === remembered) : undefined
  return stillAMember ?? orgs.find((o) => o.is_default_org) ?? orgs[0] ?? null
}
